/**
 * @module features/visual/visualPresentation
 *
 * Labels and tag colors for checkpoint outcomes and AI verdicts.
 */
import type { TagColor } from '@chatsdet/ui';
import type { VisualExplanation } from '@chatsdet/types';
import type { VisualCheckpointListItem } from '@chatsdet/types';

type Presentation = { label: string; color: TagColor };

const OUTCOMES: Record<NonNullable<VisualCheckpointListItem['outcome']>, Presentation> = {
  changed: { label: 'Changed', color: 'orange' },
  healed: { label: 'Healed', color: 'teal' },
  new: { label: 'New', color: 'blue' },
  unstable: { label: 'Unstable', color: 'yellow' },
  cant_compare: { label: "Can't compare", color: 'gray' },
  unchanged: { label: 'Unchanged', color: 'green' },
  resolved: { label: 'Resolved', color: 'green' },
};

/** A null outcome means the compare job has not finished (or was re-queued by a revert). */
export function outcomePresentation(outcome: VisualCheckpointListItem['outcome']): Presentation {
  return outcome ? OUTCOMES[outcome] : { label: 'Comparing', color: 'purple' };
}

const VERDICTS: Record<VisualExplanation['verdict'], Presentation> = {
  real: { label: 'Real change', color: 'orange' },
  matches_expected: { label: 'Matches expected', color: 'green' },
  unsure: { label: 'Unsure', color: 'gray' },
};

const INTENTS: Record<VisualExplanation['intent'], Presentation> = {
  intended: { label: 'Intended', color: 'green' },
  likely_bug: { label: 'Likely bug', color: 'red' },
  unclear: { label: 'Unclear intent', color: 'gray' },
};

export const verdictPresentation = (verdict: VisualExplanation['verdict']) => VERDICTS[verdict];
export const intentPresentation = (intent: VisualExplanation['intent']) => INTENTS[intent];

/** Row title: the flow name for native checkpoints, spec file for Playwright ones. */
export function checkpointSubject(item: VisualCheckpointListItem): { primary: string; secondary: string | null } {
  if (item.source === 'native') return { primary: item.workflowName ?? 'Flow', secondary: null };
  return { primary: item.specFile ?? 'Playwright test', secondary: item.testTitle };
}
